import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ChevronRight, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useKarolOperationalInsight } from "./useKarolOperationalInsight";

const PRIORITY_STYLES = {
  high:   { label: "Alta",  ring: "ring-amber-500/40", badge: "bg-amber-500/15 text-amber-200" },
  medium: { label: "Média", ring: "ring-slate-500/30", badge: "bg-slate-500/20 text-slate-300" },
  low:    { label: "Baixa", ring: "ring-slate-600/25", badge: "bg-slate-600/20 text-slate-400" },
};

const DISMISS_TTL_MS = 30 * 60 * 1000;

function dismissKey(rec) {
  return `karol-dismiss-${rec?.id ?? rec?.title ?? "default"}`;
}

function isDismissed(rec) {
  if (!rec) return false;
  try {
    const raw = sessionStorage.getItem(dismissKey(rec));
    if (!raw) return false;
    const { at } = JSON.parse(raw);
    return Date.now() - at < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

function storeDismiss(rec) {
  if (!rec) return;
  try {
    sessionStorage.setItem(dismissKey(rec), JSON.stringify({ at: Date.now() }));
  } catch {}
}

function canSeeKarolOperational(user, isOwner) {
  return isOwner || user?.role === "admin";
}

function timeAgo(isoStr) {
  if (!isoStr) return null;
  try {
    const diffMin = Math.floor((Date.now() - new Date(isoStr).getTime()) / 60000);
    if (diffMin < 1) return "agora";
    if (diffMin < 60) return `há ${diffMin} min`;
    return `há ${Math.floor(diffMin / 60)}h`;
  } catch {
    return null;
  }
}

export function KarolOperationalBanner() {
  const navigate = useNavigate();
  const { authAxios, user, isOwner } = useAuth();
  const restaurantId = user?.restaurant_id;
  const enabled = canSeeKarolOperational(user, isOwner);
  const { recommendation, generatedAt } = useKarolOperationalInsight(authAxios, restaurantId, { enabled });

  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(isDismissed(recommendation));
  }, [recommendation]);

  if (!enabled || !recommendation || dismissed) return null;

  const priority = (recommendation.priority || "low").toLowerCase();
  const style = PRIORITY_STYLES[priority] ?? PRIORITY_STYLES.low;
  const timeLabel = timeAgo(generatedAt);

  const handleDismiss = () => {
    storeDismiss(recommendation);
    setDismissed(true);
  };

  return (
    <div
      className={`mx-2 md:mx-3 mt-2 mb-1 rounded-xl border border-slate-700/80 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 shadow-lg ring-1 ${style.ring}`}
      data-testid="karol-operational-banner"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-slate-800/80 border border-slate-600/50 flex items-center justify-center shrink-0">
            <Sparkles className="w-4 h-4 text-amber-400/90" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-[10px] font-semibold tracking-wide text-slate-500">Karol observa</span>
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${style.badge}`}>{style.label}</span>
              {timeLabel && <span className="text-[10px] text-slate-600">{timeLabel}</span>}
            </div>
            <p className="text-sm font-medium text-slate-100 leading-snug line-clamp-2 sm:line-clamp-1">
              {recommendation.title}
            </p>
            {recommendation.description && (
              <p className="text-xs text-slate-400 mt-0.5 line-clamp-2 leading-relaxed">
                {recommendation.description}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => navigate("/intelligence?tab=karol")}
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-200 bg-slate-800/90 hover:bg-slate-700 border border-slate-600/60 rounded-lg px-3 py-2 transition-colors"
            data-testid="karol-operational-cta"
          >
            Ver Karol Central
            <ChevronRight className="w-3.5 h-3.5 opacity-70" />
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200 border border-slate-700/60 hover:border-slate-500 rounded-lg px-3 py-2 transition-colors"
            data-testid="karol-operational-dismiss"
            aria-label="Descartar"
          >
            <X className="w-3 h-3" />
            <span className="hidden sm:inline">Descartar</span>
          </button>
        </div>
      </div>
    </div>
  );
}
